import { Injectable } from "@angular/core";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import { EMPTY, forkJoin, Observable, of, Subject, throwError } from "rxjs";
import { SocketMessagesService } from "../core/socket/socket.service";
import { catchError, map, pluck, switchMap, switchMapTo, tap } from "rxjs/operators";
import { AuthService } from "../core/auth.service";
import {
  IBillingEntity,
  IBooking,
  IBookingAudioInput,
  IBookingRecurrence,
  IBookingSession,
  ICaptionValue,
  IFullBooking,
  IGooglePaymentPayload,
  IPrepMaterial,
  ISession,
  ITranscript,
  TBookingStatus,
} from "../types";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { TokenStorageService } from "../core/token-storage.service";
import { environment } from "../../environments/environment";
import { isAiLiveLexiMeetingPlatformAudioSourceName } from "../constants/ai-live";
import fileDownload from "js-file-download";
import { CustomErrorHandlerService } from "../core/custom-error-handler.service";
import { CapabilitiesService } from "../main/capabilities.service";
import { Guid } from "guid-typescript";
import moment from "moment-timezone";
import { getAppToken, getBackendBaseEndpoint, normalizeEpochToSeconds } from "../main/utils";
import { CommonService } from "./common.service";
import { ConfirmationService, IConfirmation } from "./confirmation.service";

interface IBookingsListRequest {
  billing_entity_id: number;
  start_num: string;
  count: string;
  search_by?: string;
  status?: TBookingStatus;
  time_zone?: string;
  include_past_sessions?: 0 | 1;
  filters: IBookingFilters;
  total_records?: number;
  page?: number;
  limit?: number;
}

interface IBookingRequest {
  live_booking_id: number;
}

interface IBookingsListResponse {
  start_num: number; // position of first record
  returned_count: number; // number of records returned in this call. If
  bookings: IBooking[];
}

interface IBookingsRunningResponse {
  bookings: IFullBooking[];
}

export interface IBookingResponse {
  bookings: IBooking[];
}

export interface ICreateBooking {
  billing_entity_id?: number; // billing entity ID. If not provided, will be     set to the billing entity of the user.
  title: string; // name of the meeting / lecture / stream
  short_title?: string; // short name of the session, typically used for university lecture series
  start_epoch: number; // start date and time of the first session in Unix epoch     (broadly UTC) or from when sessions are valid for recurring sessions
  duration_mins: number; // number of minutes this session runs for
  booking_product_sku: string; // product SKU URL, uuencode simulated,     describes the main session being provided
  attendees?: string; // , list of names of attendees
  fixed_fee_product_sku?: string; // product SKU URL, uuencode simulated
  fixed_fee_override?: number; // fixed fee in org_currency for each     booking
  time_zone_override?: string; // if present, this time zone will be used for this session rather than the default for the organisation or the user, if it is set. Session time_zone overrides all, user time_zone overrides organisation time_zone.
  rate_override?: number; // hourly rate in org_currency. This rate will be     used instead of org_rate, if rate_override is present.
  recurrence?: IBookingRecurrence;
  tags?: string;
  captioner_notes?: string;
  billing_contact_email?: string;
  billing_reference_override?: string;
  destination_language?: string;
  source_language?: string;
  selectedServiceType?: string;
  subServices?: string;
  sign_language_type?: string;
  language_mode?: string;
  open_access?: number;
  topic_model_language?: string;
  topic_model_language_other?: string;
  topic_model_update_existing?: number;
  topic_model_curation_notes?: string;
  person_using_vri?: string;
  presenting_participant?: string;
  num_interpreters_needed?: string;
  preferred_interpreters?: string;
  interpreter_attire?: string;
  event_recorded_live_broadcast?: string;
  allowed_viewer?: string;
  viewerGuid?: string;
  allow_transcript?: number;
  machine_translation?: number;
  booked_for_user?: number;
  point_of_contact?: string;
  shareable_caption_link?: number;
  google_payment_method?: IGooglePaymentPayload[];
  google_viewers?: number;
  google_docs_transcript_link?: string;
  audio_input?: IBookingAudioInput;
  caption_outputs?: ICaptionValue[];
  consumers?: number[];
  instant_lexi_translations?: string;
  estimated_viewer_count?: number;
}

interface IBookingHistoryItem {
  history_id: number; // ID of history change
  altered_by_user_id: number; // ID of user that made the change
  change_description: string; // human-readable text     describing the change
  change_epoch: Date; // Unix epoch of when change is recorded
  session_id?: number | null; // ID of the session this change applies to, if any
}

type IBookingHistoryItemwDate = Omit<IBookingHistoryItem, "change_epoch"> & { change_epoch: number };

// Used in getBookingHistory response
export interface IBookingHistory {
  live_booking_id: number;
  history: IBookingHistoryItemwDate[];
}

export type IFullBookingHistoryItem = Omit<IBookingHistoryItemwDate, "altered_by_user_id"> & {
  altered_by_user: string;
};

// type IFullBookingHistory = Omit<IBookingHistory, 'history'> & { history: FullBookingHistoryItem[] };

export interface IHistoryDialogData {
  title: string;
  history: IFullBookingHistoryItem[];
}

export interface ISessionDialogData {
  title: string;
  time_zone: string;
  sessions: IBookingSession[];
}

export interface ISessionDate {
  date: string;
  excluded?: 1;
}

interface IBookingConsumers {
  consumers: number[];
  live_booking_id: number;
}

export interface IBookingFilters {
  search: string;
  from: number;
  to: number;
  sort: string;
  filter_by: string;
  combine_sessions?: boolean;
  transcripts_only?: boolean;
}

const fileURL = getBackendBaseEndpoint("/v1/files");

@Injectable({
  providedIn: "root",
})
export class BookingsService {
  // @todo Not sure that it's the best and the only way to reload bookings list after creation
  // here it's used only to bind local services CreateBooking and BookingsTable
  public reload$$: Subject<void> = new Subject<void>();
  private instantLexi: any = {};
  private InstantLexiName = "";

  constructor(
    private auth: AuthService,
    private messages: SocketMessagesService,
    private errors: CustomErrorHandlerService,
    private http: HttpClient,
    private token: TokenStorageService,
    private capabilities: CapabilitiesService,
    private common: CommonService,
    private confirmation: ConfirmationService,
  ) {
    this.capabilities.instantLexi$$.subscribe((data: any) => {
      this.instantLexi = data;
      data?.forEach((i: any) => {
        if (i?.srv_id === "inl") {
          this.InstantLexiName = i?.name;
        }
      });
    });
  }

  static calcBookingCost$(
    sessionCount: number,
    sessionRealDuration: number,
    billingEntity: IBillingEntity,
    isUSTimeZone$: Observable<boolean>,
  ): Observable<number> {
    return isUSTimeZone$.pipe(
      map((isUSTimeZone) => {
        // minutes
        let sessionPaidDuration: number = sessionRealDuration;
        const minimumPaidDuration = isUSTimeZone
          ? (billingEntity.min_charge_us ?? billingEntity.min_charge)
          : billingEntity.min_charge;
        const minimumPaidChunk = isUSTimeZone
          ? (billingEntity.cost_round_us ?? billingEntity.cost_round)
          : billingEntity.cost_round;
        const hourlyRate = isUSTimeZone
          ? (billingEntity.hourly_rate_us ?? billingEntity.hourly_rate)
          : billingEntity.hourly_rate;

        if (sessionRealDuration < minimumPaidDuration) {
          sessionPaidDuration = minimumPaidDuration;
        } else if (sessionRealDuration % minimumPaidChunk) {
          sessionPaidDuration = sessionRealDuration + (minimumPaidChunk - (sessionRealDuration % minimumPaidChunk));
        }
        return Number(
          (sessionCount * ((sessionPaidDuration / 60) * hourlyRate + environment.booking.fixedFee)).toFixed(2),
        );
      }),
    );
  }

  getBookings$(
    billingEntityId: number,
    startNum: string | number,
    count: string | number,
    status: TBookingStatus,
    filters: IBookingFilters,
  ): Observable<IBookingsListResponse> {
    const { combine_sessions, transcripts_only, ...restFilters } = filters;
    let obj: Observable<IBookingsListResponse>;
    const data: IBookingsListRequest = {
      billing_entity_id: parseInt(billingEntityId.toString()),
      start_num: startNum as string,
      count: count as string,
      time_zone: this.auth.getUserTimeZone(),
      filters: restFilters,
      limit: 500,
    };

    // if (status !== 'all') {
    //     data.status = status;
    // }
    data.status = status;
    if (["running"].includes(status)) {
      data.include_past_sessions = 1;
    }
    // @ts-ignore
    obj = this.messages.request$<IBookingsListResponse>("getBookings", data).pipe(
      map(({ returned_count, start_num, bookings }: any) => {
        return {
          returned_count,
          start_num,
          bookings: bookings.map((booking: any) => {
            if (status === "upcoming" && booking.sessions?.length) {
              booking.start_epoch = booking.sessions[0].start_epoch * 1000;
            } else {
              booking.start_epoch *= 1000;
            }
            return booking;
          }),
        };
      }),
    );

    if (["completed"].includes(status)) {
      data.include_past_sessions = 1;

      const wsMessage = transcripts_only ? "getTranscriptBookings" : "getCompletedBookings";

      // @ts-ignore
      obj = this.messages.request$<IBookingsListResponse>(wsMessage, data).pipe(
        map(({ returned_count, start_num, bookings }: any) => {
          let transcripts: any = {};
          let newBookings = bookings.map((booking: any) => {
            booking.start_epoch *= 1000;
            if (booking.transcripts?.length) {
              const lbi = booking.live_booking_id;
              transcripts = transcripts[lbi]
                ? { ...transcripts, [lbi]: [...transcripts[lbi], ...booking.transcripts] }
                : { ...transcripts, [lbi]: [...booking.transcripts] };
            }

            return booking;
          });

          if (combine_sessions) {
            newBookings = this.combineSessions(newBookings, transcripts);
          }

          return {
            returned_count,
            start_num,
            bookings: newBookings,
          };
        }),
      );
    }
    // LBP-360: request$ can error (a non-2xx response, or a malformed one whose destructure
    // above throws). getBookings$ has multiple callers with different recovery needs (e.g.
    // checkAndHandleMeetingUrlOverlap$'s forkJoin relies on the error reaching its own
    // catchError), so this only makes the failure visible and re-throws - it's each caller's
    // job to decide whether/how to recover, not getBookings$'s.
    return obj.pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service17");
        return throwError(() => err);
      }),
    );
  }

  combineSessions(bookings: IFullBooking[], transcripts: { [key: string]: ITranscript[] }) {
    // istanbul ignore next -- lodash uniqBy always returns an array, never null/undefined,
    // so the || [] fallback is dead.
    return (uniqBy(bookings, "live_booking_id") || []).map((booking: IFullBooking) => {
      // istanbul ignore next -- lodash sortBy always returns an array, never null/undefined,
      // so the || [] fallback is dead.
      const newTranscripts = sortBy(transcripts[booking.live_booking_id], ["session_id"], ["asc"]) || [];

      return {
        ...booking,
        session_id: newTranscripts[0] ? newTranscripts[0].session_id : booking.session_id,
        transcripts: newTranscripts,
      };
    });
  }

  hasTranscript(booking: IFullBooking): boolean {
    return !!(booking.transcripts && booking.transcripts.length > 0);
  }

  getSingleBooking$(live_booking_id: number): Observable<IBookingResponse> {
    const data: IBookingRequest = {
      live_booking_id: live_booking_id,
    };

    // @ts-ignore
    return this.messages.request$<IBookingResponse>("getBooking", data).pipe(
      map(({ bookings }: any) => {
        return {
          bookings: bookings.map((booking: any) => {
            booking.start_epoch *= 1000;
            return booking;
          }),
        };
      }),
    );
  }

  getRunningBookings$(
    billingEntityId: number,
    startNum: string | number,
    count: string | number,
    status: TBookingStatus,
    filters: IBookingFilters,
  ): Observable<IBookingsRunningResponse> {
    let obj: Observable<IBookingsRunningResponse>;
    const data: IBookingsListRequest = {
      billing_entity_id: parseInt(billingEntityId.toString()),
      start_num: startNum as string,
      count: count as string,
      time_zone: this.auth.getUserTimeZone(),
      filters,
      limit: 500,
    };

    data.status = status;
    if (["running"].includes(status)) {
      data.include_past_sessions = 1;
    }

    // @ts-ignore
    return this.messages.request$<IBookingsRunningResponse>("getRunningBookings", data).pipe(
      map(({ bookings }: any) => {
        return {
          bookings: bookings.map((booking: any) => {
            booking.start_epoch *= 1000;
            return booking;
          }),
        };
      }),
      // LBP-360: unlike getBookings$, no caller of getRunningBookings$ needs the error itself
      // (bookings-table.component.ts's initialCall$ has no recovery of its own and would die
      // permanently without this; its poll$ already has a redundant catchError) - so this
      // swallows after surfacing the error visibly, instead of re-throwing.
      catchError((err) => {
        this.errors.handleError(err, "@booking.service18");
        return EMPTY;
      }),
    );
  }

  cancelBooking$(payload: { live_booking_id: number; session_ids?: number[] }): Observable<unknown> {
    const { live_booking_id, session_ids } = payload;
    return this.messages.request$("cancelBooking", { live_booking_id }).pipe(
      tap(() => {
        const { user_id, first_name, last_name } = this.auth.getUserInfo();
        session_ids?.forEach((session_id) => {
          this.common
            .setAnalytics$({
              user_id,
              action_type: "booking_delete",
              timestamp: moment().toISOString(),
              session_id,
              details: {
                message: `User ${first_name} ${last_name} (${user_id}) deleted a booking (${live_booking_id}).`,
                live_booking_id,
              },
            })
            .subscribe();
        });
      }),
      catchError((error) => {
        this.errors.handleError(error, "@booking.service1");
        return EMPTY;
      }),
    );
  }

  cancelSession$(session_id: number): Observable<unknown> {
    return this.messages.request$("cancelSession", { session_id }).pipe(
      tap(() => {
        const { user_id, first_name, last_name } = this.auth.getUserInfo();
        this.common
          .setAnalytics$({
            user_id,
            action_type: "session_delete",
            timestamp: moment().toISOString(),
            session_id,
            details: {
              message: `User ${first_name} ${last_name} (${user_id}) deleted a session (${session_id}).`,
              session_id,
            },
          })
          .subscribe();
      }),
      catchError((err) => {
        this.errors.handleError(err, "@booking.service2");
        return EMPTY;
      }),
    );
  }

  createBooking$(newBooking: ICreateBooking): Observable<{ live_booking_id: number; sessions: number[] } | never> {
    // @ts-ignore
    return this.messages.request$<{ live_booking_id: number }>("createBooking", newBooking).pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service3");
        return EMPTY;
      }),
    );
  }

  public getBookingSessionLink$(payload: { live_booking_id: number; session_id: number }) {
    return this.messages.request$("getBookingSessionLink", payload).pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service16");
        return EMPTY;
      }),
    );
  }

  createInstantLexiBooking$(newBooking: {
    instant_lexi_translations: string;
    captioner_notes: string;
    duration_mins: number;
    title: string;
  }) {
    const {
      // captioner_notes,
      instant_lexi_translations,
      duration_mins,
      title,
    } = newBooking;
    const { user_id, time_zone, time_zone_override } = this.auth.getUserInfo();
    const billing_entity_id = this.auth.resolveBookingDefaultsBillingEntityId();

    let book_def = this.auth.getBookingDefaults();

    const {
      // notes,
      point_of_contact,
    } = book_def;

    const zone = time_zone_override || time_zone;

    const instantLexiLanguages = instant_lexi_translations ? instant_lexi_translations.split(", ").filter(Boolean) : [];
    const machine_translation = instantLexiLanguages.length > 1 ? 1 : 0;

    const payload = {
      billing_entity_id,
      booked_for_user: user_id,
      point_of_contact: point_of_contact,
      title,
      duration_mins,
      start_epoch: normalizeEpochToSeconds(
        moment().tz(zone).add(environment.instant_lexi.create_lead_mins, "m").unix(),
      ),

      selectedServiceType: this.InstantLexiName,
      booking_product_sku: "unknown",
      viewerGuid: String(Guid.create()).replace(/[\[\]-]+/g, ""),
      tags: "event",

      allow_transcript: 1,
      allowed_viewer: "",
      attendees: "",
      captioner_notes: "",
      destination_language: instant_lexi_translations,
      event_recorded_live_broadcast: "",
      fixed_fee_product_sku: "live-captions-surcharge",
      google_docs_transcript_link: "",
      google_payment_method: [],
      google_viewers: 0,
      interpreter_attire: "",
      language_mode: "",
      machine_translation,
      num_interpreters_needed: "",
      open_access: 1,
      person_using_vri: "",
      preferred_interpreters: "",
      presenting_participant: "",
      shareable_caption_link: 0,
      short_title: "",
      sign_language_type: "",
      subServices: "",
      instant_lexi_translations,
      estimated_viewer_count: 29,
    };

    return this.createBooking$(payload).pipe(
      tap((response) => {
        const { sessions } = response;
        const { user_id, first_name, last_name } = this.auth.getUserInfo();

        sessions?.forEach((session_id) => {
          this.common
            .setAnalytics$({
              user_id,
              action_type: "booking_create_instant_lexi",
              timestamp: moment().toISOString(),
              session_id,
              details: {
                message: `User ${first_name} ${last_name} (${user_id}) created a new instant lexi booking (${response.live_booking_id}).`,
                booking: payload,
              },
            })
            .subscribe();
        });
      }),
      catchError((err) => {
        this.errors.handleError(err, "@booking.service14");
        return EMPTY;
      }),
    );
  }

  setBookingAudioInput$(data: any /*IBookingAudioInput & { live_booking_id: number }*/): Observable<unknown> {
    if (data.source_languages === null) {
      delete data.source_languages;
    }

    if (data.audio_input_params) {
      delete data.audio_input_params;
    }

    if (data.booking_audio_input_id) {
      delete data.booking_audio_input_id;
    }
    return this.messages.request$("setBookingAudioInput", data).pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service4");
        return EMPTY;
      }),
    );
  }

  addBookingCaptionOutput$(data: /*IBookingCaptionOutput */ any & { live_booking_id: number }): Observable<unknown> {
    if (data.booking_caption_output_id) {
      delete data.booking_caption_output_id;
    }

    if (typeof data.destination_languages !== "string" && data.destination_languages === null) {
      data.destination_languages = "";
    }
    return this.messages.request$("addBookingCaptionOutput", data).pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service5");
        return EMPTY;
      }),
    );
  }

  delBookingCaptionOutput$(booking_caption_output_id: number): Observable<unknown> {
    return this.messages.request$("delBookingCaptionOutput", { booking_caption_output_id }).pipe(
      catchError((err) => {
        this.errors.handleError(err, "@booking.service6");
        return EMPTY;
      }),
    );
  }

  getBookingHistory$(live_booking_id: number, session_id?: number): Observable<IBookingHistoryItemwDate[]> {
    return this.messages
      // @ts-ignore
      .request$<IBookingHistory>("getBookingHistory", {
        live_booking_id,
        ...(session_id !== undefined ? { session_id } : {}),
      })
      .pipe(
        pluck("history"),
        map((history: any) =>
          history.map((item: any) => {
            return Object.assign({}, item, { change_epoch: new Date(item.change_epoch * 1000) });
          }),
        ),
        catchError((err) => {
          this.errors.handleError(err, "@booking.service7");
          return EMPTY;
        }),
      );
  }

  // @todo should be detailed description of type
  updateBooking$(bookingData: Partial<IBooking>): Observable<unknown> {
    const { session_ids, ...restBookingData } = bookingData;
    return this.messages.request$("updateBooking", restBookingData).pipe(
      tap(() => {
        const { user_id, first_name, last_name } = this.auth.getUserInfo();

        session_ids?.forEach(async (session_id) => {
          this.common
            .setAnalytics$({
              user_id,
              action_type: "booking_update",
              timestamp: moment().toISOString(),
              session_id,
              details: {
                message: `User ${first_name} ${last_name} (${user_id}) updated a booking (${bookingData.live_booking_id}).`,
                booking: bookingData,
              },
            })
            .subscribe();
        });
      }),
      catchError((err) => {
        // LBP-333: still log/handle session-expiry etc. via the shared handler, but let the
        // failure propagate so BookingsListService.edit$() — which has the booking context and
        // runs after the dialog has already closed and navigated away — can show an explicit,
        // edit-specific failure notification instead of this disappearing silently.
        this.errors.handleError(err, "@booking.service8");
        return throwError(() => err);
      }),
    );
  }

  // @todo should be detailed description of type
  updateLBPSession$(session_data: Partial<ISession>): Observable<unknown> {
    return this.messages.request$("updateLBPSession", session_data).pipe(
      tap(() => {
        const { user_id, first_name, last_name } = this.auth.getUserInfo();
        this.common
          .setAnalytics$({
            user_id,
            action_type: "session_update",
            timestamp: moment().toISOString(),
            session_id: session_data.session_id,
            details: {
              message: `User ${first_name} ${last_name} (${user_id}) updated a session (${session_data.session_id}).`,
              session: session_data,
            },
          })
          .subscribe();
      }),
    );
  }

  // @todo should be detailed description of type
  async uploadSessionPrepMaterial$(payload: {
    org_id: number;
    session_id: number;
    to_delete: IPrepMaterial[];
    new_prep: IPrepMaterial[];
    cb: Function;
  }) {
    const { org_id, session_id, to_delete, new_prep, cb } = payload;
    const token = JSON.parse(localStorage.getItem("app_token") || "");
    const { user_id, first_name, last_name } = this.auth.getUserInfo();

    if (new_prep?.length) {
      new_prep.forEach(async (i: IPrepMaterial) => {
        const { filename, fileData } = i;
        const data = { session_id, org_id, original_filename: filename };
        const mtdt = JSON.stringify(data);

        const formData = new FormData();
        formData.append("file", fileData as File);
        formData.append("org_id", org_id.toString());
        formData.append("type", "sess_prep_material");
        formData.append("metadata", mtdt);

        await fetch(fileURL, {
          method: "post",
          headers: { Authorization: "Bearer " + token },
          body: formData,
        })
          .then((response) => {
            this.common
              .setAnalytics$({
                user_id,
                action_type: "session_prep_mat_upload",
                timestamp: moment().toISOString(),
                session_id,
                details: {
                  message: `User ${first_name} ${last_name} (${user_id}) uploaded a prep_material for session (${session_id}).`,
                  data,
                },
              })
              .subscribe();
            return response.json();
          })
          .then((data) => {
            if (cb) {
              cb(data, "create");
            }
          })
          .catch((err) => {
            console.log(err);
          });
      });
    }

    if (to_delete?.length) {
      await fetch(fileURL, {
        method: "delete",
        headers: {
          Authorization: "Bearer " + getAppToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          files: to_delete,
        }),
      })
        .then(() => {
          const prep_material_ids = to_delete.map((i) => i.id);
          const data = { session_id, org_id, prep_material_ids };
          this.common
            .setAnalytics$({
              user_id,
              action_type: "session_prep_mat_delete",
              timestamp: moment().toISOString(),
              session_id,
              details: {
                message: `User ${first_name} ${last_name} (${user_id}) deleted a prep_material for session (${session_id}).`,
                data,
              },
            })
            .subscribe();
          if (cb) {
            cb(
              {
                session_id,
                to_delete: to_delete.map((i) => i.id),
                success: true,
              },
              "delete",
            );
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  getBillingEntityCommonExclusionDates$(billing_entity_id: number): Observable<string[]> {
    return (
      this.messages
        // @ts-ignore
        .request$<{ exclusion_dates: string[] }>("getBillingEntityCommonExclusionDates", { billing_entity_id })
        .pipe(pluck("exclusion_dates"))
    );
  }

  addConsumersToBooking$(data: IBookingConsumers): Observable<unknown> {
    if (data.consumers && data.consumers.length) {
      return this.messages.request$("addConsumersToBooking", data).pipe(
        catchError((error) => {
          this.errors.handleError(error, "@booking.service9");
          return EMPTY;
        }),
      );
    }
    return of(true);
  }

  getConsumersOfBooking$(live_booking_id: number): Observable<number[]> {
    // @ts-ignore
    return this.messages.request$<{ consumers: number[] }>("getConsumersOfBooking", { live_booking_id }).pipe(
      pluck("consumers"),
      catchError((error) => {
        this.errors.handleError(error, "@booking.service10");
        return EMPTY;
      }),
    );
  }

  delConsumersFromBooking$(live_booking_id: number, consumers: number[]): Observable<unknown> {
    return this.messages.request$("delConsumersFromBooking", { live_booking_id, consumers }).pipe(
      catchError((error) => {
        this.errors.handleError(error, "@booking.service11");
        return EMPTY;
      }),
    );
  }

  getPrepMaterial$(live_booking_id: number): Observable<IPrepMaterial[]> {
    // @ts-ignore
    return this.messages.request$<{ prep_materials: IPrepMaterial[] }>("getPrepMaterial", { live_booking_id }).pipe(
      pluck("prep_materials"),
      catchError((error) => {
        this.errors.handleError(error, "@booking.service12");
        return EMPTY;
      }),
    );
  }

  getTranScript$(transcript_id: number): Observable<ITranscript[]> {
    // @ts-ignore
    return this.messages.request$<{ transcript: ITranscript[] }>("getTranscripts", { transcript_id }).pipe(
      pluck("transcript"),
      catchError((error) => {
        this.errors.handleError(error, "@booking.service13");
        return EMPTY;
      }),
    );
  }

  uploadPrepMaterials$(live_booking_id: number, files: File[]): Observable<unknown> {
    if (!files.length) {
      return of(true);
    }
    const uploadUrl =
      (environment.apiServer.slice(-1) === "/" ? environment.apiServer.slice(0, -1) : environment.apiServer) +
      "/v1/prep-material/upload";

    const requests = files.map((file) => {
      const formData = new FormData();
      formData.append("filedata", file);
      formData.append("live_booking_id", live_booking_id.toString());

      return this.http
        .post<unknown>(uploadUrl, formData, {
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.token.get(),
          }),
        })
        .pipe(
          tap(({ prep_material_id }: any) => {
            const { user_id, first_name, last_name } = this.auth.getUserInfo();
            this.common
              .setAnalytics$({
                user_id,
                action_type: "prep_material_upload",
                timestamp: moment().toISOString(),
                details: {
                  message: `User ${first_name} ${last_name} (${user_id}) uploaded prep_material (${prep_material_id}) for booking (${live_booking_id}).`,
                  prep_material_id,
                  live_booking_id,
                },
              })
              .subscribe();
          }),
        );
    });
    return forkJoin(requests).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(new Error(errorResponse.message));
      }),
    );
  }

  delPrepMaterial$(prep_material_id: number): Observable<unknown> {
    return this.messages.request$("delPrepMaterial", { prep_material_id }).pipe(
      tap(() => {
        const { user_id, first_name, last_name } = this.auth.getUserInfo();
        this.common
          .setAnalytics$({
            user_id,
            action_type: "prep_material_delete",
            timestamp: moment().toISOString(),
            details: {
              message: `User ${first_name} ${last_name} (${user_id}) deleted prep_material (${prep_material_id}).`,
              prep_material_id,
            },
          })
          .subscribe();
      }),
    );
  }

  downloadPrepMaterial$(prep_material_id: number, fileName: string): Observable<never> {
    let headers = new HttpHeaders();
    headers = headers.append("Authorization", "Bearer " + this.token.get());

    const params = new HttpParams();
    const url =
      environment.apiServer +
      "/v1/prep-material/download?" +
      params.append("prep_material_id", prep_material_id.toString()).toString();
    return this.http
      .get(url, {
        responseType: "blob",
        headers,
      })
      .pipe(
        tap((data) => {
          fileDownload(data, fileName);
        }),
        tap(() => {
          const { user_id, first_name, last_name } = this.auth.getUserInfo();
          this.common
            .setAnalytics$({
              user_id,
              action_type: "prep_material_download",
              timestamp: moment().toISOString(),
              details: {
                message: `User ${first_name} ${last_name} (${user_id}) downloaded prep_material (${prep_material_id}).`,
                prep_material_id,
              },
            })
            .subscribe();
        }),
        switchMapTo(EMPTY),
      );
  }

  uploadKnowledgeBase$(org_id: number, files: File[]): Observable<unknown> {
    if (!files.length) {
      return of(true);
    }
    const uploadUrl =
      (environment.apiServer.slice(-1) === "/" ? environment.apiServer.slice(0, -1) : environment.apiServer) +
      "/v1/knowledge-base";

    const requests = files.map((file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("org_id", org_id.toString());

      return this.http
        .post<unknown>(uploadUrl, formData, {
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.token.get(),
          }),
        })
        .pipe(
          tap(() => {
            const { user_id, first_name, last_name } = this.auth.getUserInfo();
            this.common
              .setAnalytics$({
                user_id,
                action_type: "knowledge_base_upload",
                timestamp: moment().toISOString(),
                details: {
                  message: `User ${first_name} ${last_name} (${user_id}) uploaded knowledge_base (${file.name}) for org_id(${org_id}).`,
                  org_id,
                  filename: file.name,
                },
              })
              .subscribe();
          }),
        );
    });
    return forkJoin(requests).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(new Error(errorResponse.message));
      }),
    );
  }

  stripeSetupIntent$(live_booking_id: number): Observable<string> {
    return (
      this.messages
        // @ts-ignore
        .request$<{ intent: any }>("stripeSetupIntent", { live_booking_id })
        .pipe(pluck("stripe_client_secret"))
    );
  }

  stripeCardSetupConfirmed$(live_booking_id: number, payment_method: string): Observable<unknown> {
    return this.messages.request$("stripeCardSetupConfirmed", { live_booking_id, payment_method });
  }

  setPostpayPaymentType(live_booking_id: number): Observable<unknown> {
    return this.messages.request$("setPostpayPaymentType", { live_booking_id });
  }

  // LBP-185: Prevent overlapping Ai-Live LEXI bookings with same meeting URL
  public checkAndHandleMeetingUrlOverlap$(
    selectedServiceType: string,
    audioInput: IBookingAudioInput | undefined,
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    billingEntityId?: number,
    excludeBookingId?: number,
  ): Observable<boolean> {
    if (!["Ai-Live Lexi", "Ai-Live LEXI", "LEXI Access"].includes(selectedServiceType)) {
      return of(true);
    }

    const audioName = audioInput?.audio_name || "";
    if (!isAiLiveLexiMeetingPlatformAudioSourceName(audioName)) {
      return of(true);
    }

    const meetingUrl = this.extractMeetingUrl(audioInput);
    if (!meetingUrl) {
      return of(true);
    }

    const beId = billingEntityId || this.auth.getUserBillingEntityId();
    const defaultFilters: IBookingFilters = {
      search: "",
      from: 0,
      to: 0,
      sort: "",
      filter_by: "",
    };

    return forkJoin([
      this.getBookings$(beId, 0, 1000, "upcoming", defaultFilters),
      this.getBookings$(beId, 0, 1000, "running", defaultFilters),
    ]).pipe(
      map(([upcomingResponse, runningResponse]: [IBookingResponse, IBookingResponse]) => {
        const allBookings = [...upcomingResponse.bookings, ...runningResponse.bookings];
        return this.checkForOverlappingBookings(
          allBookings,
          meetingUrl,
          startDate,
          startTime,
          endTime,
          timeZone,
          excludeBookingId,
        );
      }),
      switchMap((hasOverlap) => {
        if (hasOverlap) {
          return this.showMeetingUrlOverlapDialog$().pipe(map(() => false));
        }
        return of(true);
      }),
      catchError(() => of(true)),
    );
  }

  // LBP-185: Extract meeting URL from audio params
  private extractMeetingUrl(audioInput: IBookingAudioInput | undefined): string | null {
    if (!audioInput) {
      return null;
    }

    const params = audioInput.audio_input_params || audioInput.audio_params || [];
    for (const param of params) {
      const value = param.value || "";
      // Return any non-empty value (including placeholder text like "test")
      // This ensures overlap detection works for both valid URLs and placeholder text
      if (value && value.trim()) {
        return value.trim();
      }
    }

    return null;
  }

  // LBP-185: Check for overlapping bookings with same meeting URL
  private checkForOverlappingBookings(
    bookings: IBooking[],
    meetingUrl: string,
    startDate: string,
    startTime: string,
    endTime: string,
    timeZone: string,
    excludeBookingId?: number,
  ): boolean {
    const normalizedUrl = meetingUrl.trim().toLowerCase().replace(/\/$/, "");
    const newBookingStart = moment.tz(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm", timeZone);
    const newBookingEnd = moment.tz(`${startDate} ${endTime}`, "YYYY-MM-DD HH:mm", timeZone);

    if (newBookingEnd.isBefore(newBookingStart)) {
      newBookingEnd.add(1, "day");
    }

    for (const booking of bookings) {
      if (excludeBookingId && booking.live_booking_id === excludeBookingId) {
        continue;
      }

      const serviceType = booking.service_type || booking.sessions?.[0]?.service_type || "";
      if (!["Ai-Live Lexi", "Ai-Live LEXI", "LEXI Access"].includes(serviceType)) {
        continue;
      }

      const audioInput = booking.audio_input || booking.running_audio_input;
      const audioName = audioInput?.audio_name || "";
      if (!isAiLiveLexiMeetingPlatformAudioSourceName(audioName)) {
        continue;
      }

      const existingMeetingUrl = this.extractMeetingUrl(audioInput);
      if (!existingMeetingUrl) {
        continue;
      }

      const normalizedExistingUrl = existingMeetingUrl.trim().toLowerCase().replace(/\/$/, "");
      if (normalizedExistingUrl !== normalizedUrl) {
        continue;
      }

      const bookingTimezone = booking.booking_time_zone || booking.time_zone_override || timeZone;
      const existingStart = moment(booking.start_epoch).tz(bookingTimezone);
      const existingEnd = existingStart.clone().add(booking.duration_mins, "minutes");
      const existingEndWithBuffer = existingEnd.clone().add(1, "minute");
      const existingStartWithBuffer = existingStart.clone().subtract(1, "minute");

      if (newBookingStart.isBefore(existingEndWithBuffer) && newBookingEnd.isAfter(existingStartWithBuffer)) {
        return true;
      }
    }

    return false;
  }

  // LBP-185: Show overlap error dialog
  private showMeetingUrlOverlapDialog$(): Observable<any> {
    const confirmationData: IConfirmation = {
      message:
        "There is already an existing booking with this identical meeting URL that overlaps with the booking time specified. Please update the time of this new booking to have at least 1 minute difference from the existing booking.",
      title: "Overlapping Booking",
      showActionButtons: false,
    };
    return this.confirmation.confirm$(confirmationData);
  }
}
